import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChangeEvent, ReactNode } from 'react';

import { Provider } from './context';

export type CheckboxGroupProps = {
    /**
     * Renders `ManagedCheckbox` children as checkbox options.
     * */
    children: ReactNode;
    /**
     * Sets the currently checked `ManagedCheckbox` options.
     * */
    value?: string[];
    /**
     * Sets the name attributes for the `ManagedCheckbox` options.
     * */
    name: string;
    /**
     * Function executed when a `ManagedCheckbox` option is checked / unchecked.
     * */
    onChange?: (e: ChangeEvent<HTMLInputElement>, value: string[]) => void;
    /**
     * Renders a form validation `InputErrorMessage`.
     * */
    error?: boolean;
};

const CheckboxGroup = ({ children, value: checkedValues = [], name, error, onChange }: CheckboxGroupProps) => {
    const [values, setValues] = useState(checkedValues);

    useEffect(() => {
        setValues(checkedValues);
    }, [checkedValues.join()]);

    const removeValue = useCallback(
        (originalEvent: ChangeEvent<HTMLInputElement>, value: string) => {
            const nextValues = values.includes(value) ? values.filter((item) => item !== value) : values;

            setValues(nextValues);

            if (typeof onChange === 'function') {
                onChange(originalEvent, nextValues);
            }
        },
        [onChange, values],
    );

    const addValue = useCallback(
        (originalEvent: ChangeEvent<HTMLInputElement>, value: string) => {
            const nextValues = values.includes(value) ? values : [...values, value];

            setValues(nextValues);

            if (typeof onChange === 'function') {
                onChange(originalEvent, nextValues);
            }
        },
        [onChange, values],
    );

    const changeHandler = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            e.persist();

            const {
                currentTarget: { value, checked },
            } = e;

            if (checked) {
                addValue(e, value);
            } else {
                removeValue(e, value);
            }
        },
        [addValue, removeValue],
    );

    const contextValue = useMemo(
        () => ({
            onChange: changeHandler,
            value: values,
            name,
            error,
        }),
        [changeHandler, name, values],
    );

    return <Provider value={contextValue}>{children}</Provider>;
};

CheckboxGroup.displayName = 'CheckboxGroup';

export default CheckboxGroup;
